import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  SafeAreaView,
  StatusBar,
  Modal,
  TextInput,
  Alert,
  Dimensions,
  Animated,
  Image,
} from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { MaterialCommunityIcons, Ionicons } from '@expo/vector-icons';
import AsyncStorage from '@react-native-async-storage/async-storage';
import axios from 'axios';

const { width, height } = Dimensions.get('window');

// ==================== ✅ API CONFIGURATION ====================
const API_BASE_URL = 'https://api.jobeng.ai';
const API_ENDPOINTS = {
  projects: `${API_BASE_URL}/api/projects`,
  companyProfile: `${API_BASE_URL}/api/company/profile`,
};

// ✅ Axios instance with API key and auth interceptor
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'X-API-Key': '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef',
  },
});

api.interceptors.request.use(
  async (config) => {
    try {
      const token = await AsyncStorage.getItem('auth_token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      const companyId = await AsyncStorage.getItem('company_id')
                      || await AsyncStorage.getItem('company_id');
      if (companyId) {
        config.params = { ...config.params, company_id: parseInt(companyId), company_id: parseInt(companyId) };
      }
    } catch (error) {
      console.error('Error in request interceptor:', error);
    }
    return config;
  },
  (error) => Promise.reject(error)
);

export default function ProjectScheduleScreen({ navigation, route }) {
  const { project } = route.params || {};
  
  const [companyLogo, setCompanyLogo] = useState(null);

  // Load company logo on mount
  useEffect(() => {
    loadCompanyLogo();
  }, []);

  const loadCompanyLogo = async () => {
    try {
      const cachedProfile = await AsyncStorage.getItem('company_profile');
      if (cachedProfile) {
        const profile = JSON.parse(cachedProfile);
        setCompanyLogo(profile.logo || null);
      }

      const companyId = await AsyncStorage.getItem('company_id');
      if (companyId) {
        const response = await api.get(API_ENDPOINTS.companyProfile, {
          params: { company_id: companyId },
        });
        if (response.data?.success && response.data.data && response.data.data.length > 0) {
          const latestProfile = response.data.data[0];
          setCompanyLogo(latestProfile.logo || null);
          await AsyncStorage.setItem('company_profile', JSON.stringify(latestProfile));
        }
      }
    } catch (error) {
      console.warn('Error loading company logo:', error);
    }
  };

  // Early return if no project data
  if (!project) {
    return (
      <SafeAreaView style={styles.container}>
        <StatusBar barStyle="light-content" backgroundColor="#3B82F6" />
        <View style={styles.errorContainer}>
          <MaterialCommunityIcons name="alert-circle" size={64} color="#EF4444" />
          <Text style={styles.errorTitle}>No Project Data</Text>
          <Text style={styles.errorMessage}>
            Unable to load project schedule without project information.
          </Text>
          <TouchableOpacity
            style={styles.errorButton}
            onPress={() => navigation.goBack()}
          >
            <Text style={styles.errorButtonText}>Go Back</Text>
          </TouchableOpacity>
        </View>
      </SafeAreaView>
    );
  }

  const [activeTab, setActiveTab] = useState('timeline');
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [showMilestoneModal, setShowMilestoneModal] = useState(false);
  const [showTeamModal, setShowTeamModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [viewMode, setViewMode] = useState('week'); // week, month, quarter
  const [currentDate, setCurrentDate] = useState(new Date());
  
  // Animation values
  const fadeAnim = useRef(new Animated.Value(0)).current;
  const slideAnim = useRef(new Animated.Value(50)).current;

  // Sample project data with comprehensive schedule information
  const [projectData, setProjectData] = useState({
    id: project?.id || 'proj_001',
    name: project?.name || 'Mobile App Development',
    description: project?.description || 'Complete mobile application with backend integration',
    status: project?.status || 'In Progress',
    priority: project?.priority || 'High',
    startDate: project?.startDate || '2024-01-15',
    endDate: project?.endDate || '2024-06-30',
    progress: project?.progress || 65,
    budget: project?.budget || 150000,
    spent: project?.spent || 97500,
    manager: project?.manager || 'Sarah Johnson',
    client: project?.client || 'TechCorp Inc.',
    team: project?.team || [
      { id: 1, name: 'John Smith', role: 'Lead Developer', avatar: 'https://via.placeholder.com/40', workload: 85 },
      { id: 2, name: 'Emma Wilson', role: 'UI/UX Designer', avatar: 'https://via.placeholder.com/40', workload: 70 },
      { id: 3, name: 'Mike Chen', role: 'Backend Developer', avatar: 'https://via.placeholder.com/40', workload: 90 },
      { id: 4, name: 'Lisa Brown', role: 'QA Engineer', avatar: 'https://via.placeholder.com/40', workload: 60 },
    ],
    milestones: [
      {
        id: 1,
        title: 'Project Kickoff',
        date: '2024-01-15',
        status: 'completed',
        description: 'Initial project setup and team onboarding'
      },
      {
        id: 2,
        title: 'Design Phase Complete',
        date: '2024-02-28',
        status: 'completed',
        description: 'UI/UX designs approved and finalized'
      },
      {
        id: 3,
        title: 'MVP Development',
        date: '2024-04-15',
        status: 'in-progress',
        description: 'Core features development and testing'
      },
      {
        id: 4,
        title: 'Beta Testing',
        date: '2024-05-30',
        status: 'pending',
        description: 'User acceptance testing and feedback'
      },
      {
        id: 5,
        title: 'Final Delivery',
        date: '2024-06-30',
        status: 'pending',
        description: 'Production deployment and handover'
      }
    ],
    tasks: [
      {
        id: 1,
        title: 'User Authentication System',
        description: 'Implement secure login and registration',
        assignee: 'John Smith',
        priority: 'High',
        status: 'completed',
        startDate: '2024-01-20',
        endDate: '2024-02-05',
        progress: 100,
        estimatedHours: 40,
        actualHours: 38,
        dependencies: []
      },
      {
        id: 2,
        title: 'Dashboard UI Design',
        description: 'Create responsive dashboard interface',
        assignee: 'Emma Wilson',
        priority: 'High',
        status: 'completed',
        startDate: '2024-02-01',
        endDate: '2024-02-20',
        progress: 100,
        estimatedHours: 60,
        actualHours: 65,
        dependencies: []
      },
      {
        id: 3,
        title: 'API Development',
        description: 'Build RESTful API endpoints',
        assignee: 'Mike Chen',
        priority: 'High',
        status: 'in-progress',
        startDate: '2024-02-15',
        endDate: '2024-04-10',
        progress: 75,
        estimatedHours: 120,
        actualHours: 95,
        dependencies: [1]
      },
      {
        id: 4,
        title: 'Mobile App Integration',
        description: 'Connect frontend with backend APIs',
        assignee: 'John Smith',
        priority: 'Medium',
        status: 'in-progress',
        startDate: '2024-03-01',
        endDate: '2024-04-25',
        progress: 60,
        estimatedHours: 80,
        actualHours: 52,
        dependencies: [2, 3]
      },
      {
        id: 5,
        title: 'Testing & QA',
        description: 'Comprehensive testing and bug fixes',
        assignee: 'Lisa Brown',
        priority: 'High',
        status: 'pending',
        startDate: '2024-04-20',
        endDate: '2024-05-25',
        progress: 0,
        estimatedHours: 100,
        actualHours: 0,
        dependencies: [3, 4]
      }
    ]
  });

  const [newTask, setNewTask] = useState({
    title: '',
    description: '',
    assignee: '',
    priority: 'Medium',
    startDate: '',
    endDate: '',
    estimatedHours: ''
  });

  const [newMilestone, setNewMilestone] = useState({
    title: '',
    description: '',
    date: ''
  });

  // Tab configuration
  const tabs = [
    { id: 'timeline', name: 'Timeline', icon: 'timeline' },
    { id: 'gantt', name: 'Gantt Chart', icon: 'chart-gantt' },
    { id: 'calendar', name: 'Calendar', icon: 'calendar' },
    { id: 'team', name: 'Team', icon: 'account-group' },
    { id: 'reports', name: 'Reports', icon: 'chart-line' }
  ];

  useEffect(() => {
    startAnimations();
    loadProjectData();
  }, []);

  const startAnimations = () => {
    Animated.parallel([
      Animated.timing(fadeAnim, {
        toValue: 1,
        duration: 800,
        useNativeDriver: true,
      }),
      Animated.timing(slideAnim, {
        toValue: 0,
        duration: 600,
        useNativeDriver: true,
      }),
    ]).start();
  };

  const loadProjectData = async () => {
    try {
      const savedData = await AsyncStorage.getItem(`project_schedule_${project?.id || 'default'}`);
      if (savedData) {
        setProjectData(JSON.parse(savedData));
      }
    } catch (error) {
      console.error('Error loading project data:', error);
    }
  };

  const saveProjectData = async (data) => {
    try {
      await AsyncStorage.setItem(`project_schedule_${project?.id || 'default'}`, JSON.stringify(data));
    } catch (error) {
      console.error('Error saving project data:', error);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return '#10B981';
      case 'in-progress': return '#3B82F6';
      case 'pending': return '#F59E0B';
      case 'overdue': return '#EF4444';
      default: return '#6B7280';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'High': return '#EF4444';
      case 'Medium': return '#F59E0B';
      case 'Low': return '#10B981';
      default: return '#6B7280';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const calculateProjectHealth = () => {
    const totalTasks = projectData.tasks.length;
    const completedTasks = projectData.tasks.filter(task => task.status === 'completed').length;
    const overdueTasks = projectData.tasks.filter(task => {
      const endDate = new Date(task.endDate);
      const today = new Date();
      return task.status !== 'completed' && endDate < today;
    }).length;

    return {
      completion: Math.round((completedTasks / totalTasks) * 100),
      onTrack: overdueTasks === 0,
      overdue: overdueTasks,
      efficiency: Math.round((projectData.tasks.reduce((sum, task) => 
        sum + (task.actualHours || 0), 0) / 
        projectData.tasks.reduce((sum, task) => sum + task.estimatedHours, 0)) * 100)
    };
  };

  const addNewTask = () => {
    if (!newTask.title.trim()) {
      Alert.alert('Error', 'Please enter a task title');
      return;
    }

    const task = {
      id: Date.now(),
      ...newTask,
      status: 'pending',
      progress: 0,
      actualHours: 0,
      dependencies: []
    };

    const updatedProject = {
      ...projectData,
      tasks: [...projectData.tasks, task]
    };

    setProjectData(updatedProject);
    saveProjectData(updatedProject);
    setNewTask({
      title: '',
      description: '',
      assignee: '',
      priority: 'Medium',
      startDate: '',
      endDate: '',
      estimatedHours: ''
    });
    setShowTaskModal(false);
    Alert.alert('Success', 'Task added successfully');
  };

  const addNewMilestone = () => {
    if (!newMilestone.title.trim()) {
      Alert.alert('Error', 'Please enter a milestone title');
      return;
    }

    const milestone = {
      id: Date.now(),
      ...newMilestone,
      status: 'pending'
    };

    const updatedProject = {
      ...projectData,
      milestones: [...projectData.milestones, milestone]
    };

    setProjectData(updatedProject);
    saveProjectData(updatedProject);
    setNewMilestone({
      title: '',
      description: '',
      date: ''
    });
    setShowMilestoneModal(false);
    Alert.alert('Success', 'Milestone added successfully');
  };

  const updateTaskStatus = (taskId, newStatus) => {
    const updatedTasks = projectData.tasks.map(task => {
      if (task.id === taskId) {
        return {
          ...task,
          status: newStatus,
          progress: newStatus === 'completed' ? 100 : task.progress
        };
      }
      return task;
    });

    const updatedProject = {
      ...projectData,
      tasks: updatedTasks
    };

    setProjectData(updatedProject);
    saveProjectData(updatedProject);
  };

  const renderTimelineView = () => {
    const health = calculateProjectHealth();

    return (
      <View style={styles.timelineContainer}>
        {/* Project Health Overview */}
        <View style={styles.healthCard}>
          <Text style={styles.healthTitle}>Project Health</Text>
          <View style={styles.healthMetrics}>
            <View style={styles.healthMetric}>
              <MaterialCommunityIcons name="check-circle" size={20} color="#10B981" />
              <Text style={styles.healthLabel}>Completion</Text>
              <Text style={styles.healthValue}>{health.completion}%</Text>
            </View>
            <View style={styles.healthMetric}>
              <MaterialCommunityIcons 
                name={health.onTrack ? "clock-check" : "clock-alert"} 
                size={20} 
                color={health.onTrack ? "#10B981" : "#EF4444"} 
              />
              <Text style={styles.healthLabel}>Status</Text>
              <Text style={[styles.healthValue, { color: health.onTrack ? "#10B981" : "#EF4444" }]}>
                <Text>{health.onTrack ? "On Track" : `${health.overdue} Overdue`}</Text>
              </Text>
            </View>
            <View style={styles.healthMetric}>
              <MaterialCommunityIcons name="speedometer" size={20} color="#3B82F6" />
              <Text style={styles.healthLabel}>Efficiency</Text>
              <Text style={styles.healthValue}>{health.efficiency}%</Text>
            </View>
          </View>
        </View>

        {/* Milestones */}
        <View style={styles.section}>
          <View style={styles.sectionHeader}>
            <Text style={styles.sectionTitle}>Milestones</Text>
            <TouchableOpacity
              style={styles.addButton}
              onPress={() => setShowMilestoneModal(true)}
            >
              <MaterialCommunityIcons name="plus" size={20} color="#3B82F6" />
            </TouchableOpacity>
          </View>
          
          <View style={styles.milestonesContainer}>
            {projectData.milestones.map((milestone, index) => (
              <View key={milestone.id} style={styles.milestoneItem}>
                <View style={styles.milestoneIndicator}>
                  <View style={[styles.milestoneDot, { backgroundColor: getStatusColor(milestone.status) }]} />
                  {index < projectData.milestones.length - 1 && <View style={styles.milestoneLine} />}
                </View>
                <View style={styles.milestoneContent}>
                  <View style={styles.milestoneHeader}>
                    <Text style={styles.milestoneTitle}>{milestone.title}</Text>
                    <Text style={styles.milestoneDate}>{formatDate(milestone.date)}</Text>
                  </View>
                  <Text style={styles.milestoneDescription}>{milestone.description}</Text>
                  <View style={[styles.milestoneStatus, { backgroundColor: getStatusColor(milestone.status) }]}>
                    <Text style={styles.milestoneStatusText}>
                      {milestone.status.replace('-', ' ').toUpperCase()}
                    </Text>
                  </View>
                </View>
              </View>
            ))}
          </View>
        </View>

        {/* Tasks */}
        <View style={styles.section}>
          <View style={styles.sectionHeader}>
            <Text style={styles.sectionTitle}>Tasks</Text>
            <TouchableOpacity
              style={styles.addButton}
              onPress={() => setShowTaskModal(true)}
            >
              <MaterialCommunityIcons name="plus" size={20} color="#3B82F6" />
            </TouchableOpacity>
          </View>
          
          {projectData.tasks.map((task) => (
            <TouchableOpacity
              key={task.id}
              style={styles.taskCard}
              onPress={() => setSelectedTask(task)}
            >
              <View style={styles.taskHeader}>
                <View style={styles.taskInfo}>
                  <Text style={styles.taskTitle}>{task.title}</Text>
                  <Text style={styles.taskAssignee}>Assigned to: {task.assignee}</Text>
                </View>
                <View style={styles.taskMeta}>
                  <View style={[styles.priorityBadge, { backgroundColor: getPriorityColor(task.priority) }]}>
                    <Text style={styles.priorityText}>{task.priority}</Text>
                  </View>
                  <TouchableOpacity
                    style={[styles.statusBadge, { backgroundColor: getStatusColor(task.status) }]}
                    onPress={() => {
                      const statusOptions = ['pending', 'in-progress', 'completed'];
                      const currentIndex = statusOptions.indexOf(task.status);
                      const nextStatus = statusOptions[(currentIndex + 1) % statusOptions.length];
                      updateTaskStatus(task.id, nextStatus);
                    }}
                  >
                    <Text style={styles.statusText}>
                      {task.status.replace('-', ' ').toUpperCase()}
                    </Text>
                  </TouchableOpacity>
                </View>
              </View>
              
              <Text style={styles.taskDescription}>{task.description}</Text>
              
              <View style={styles.taskProgress}>
                <View style={styles.progressInfo}>
                  <Text style={styles.progressLabel}>Progress: {task.progress}%</Text>
                  <Text style={styles.progressDates}>
                    {formatDate(task.startDate)} - {formatDate(task.endDate)}
                  </Text>
                </View>
                <View style={styles.progressBar}>
                  <View 
                    style={[
                      styles.progressFill, 
                      { 
                        width: `${task.progress}%`,
                        backgroundColor: getStatusColor(task.status)
                      }
                    ]} 
                  />
                </View>
              </View>
              
              <View style={styles.taskFooter}>
                <Text style={styles.taskHours}>
                  {task.actualHours || 0}h / {task.estimatedHours}h
                </Text>
                <MaterialCommunityIcons name="chevron-right" size={20} color="#6B7280" />
              </View>
            </TouchableOpacity>
          ))}
        </View>
      </View>
    );
  };

  const renderGanttView = () => (
    <View style={styles.ganttContainer}>
      <Text style={styles.comingSoon}>Gantt Chart View</Text>
      <Text style={styles.comingSoonDesc}>Interactive Gantt chart coming soon</Text>
    </View>
  );

  const renderCalendarView = () => (
    <View style={styles.calendarContainer}>
      <Text style={styles.comingSoon}>Calendar View</Text>
      <Text style={styles.comingSoonDesc}>Calendar integration coming soon</Text>
    </View>
  );

  const renderTeamView = () => (
    <View style={styles.teamContainer}>
      <Text style={styles.sectionTitle}>Team Members</Text>
      {projectData.team.map((member) => (
        <View key={member.id} style={styles.teamMemberCard}>
          <View style={styles.memberInfo}>
            <View style={styles.memberAvatar}>
              <Text style={styles.memberInitials}>
                {member.name.split(' ').map(n => n[0]).join('')}
              </Text>
            </View>
            <View style={styles.memberDetails}>
              <Text style={styles.memberName}>{member.name}</Text>
              <Text style={styles.memberRole}>{member.role}</Text>
            </View>
          </View>
          <View style={styles.workloadInfo}>
            <Text style={styles.workloadLabel}>Workload</Text>
            <View style={styles.workloadBar}>
              <View 
                style={[
                  styles.workloadFill, 
                  { 
                    width: `${member.workload}%`,
                    backgroundColor: member.workload > 80 ? '#EF4444' : member.workload > 60 ? '#F59E0B' : '#10B981'
                  }
                ]} 
              />
            </View>
            <Text style={styles.workloadValue}>{member.workload}%</Text>
          </View>
        </View>
      ))}
    </View>
  );

  const renderReportsView = () => (
    <View style={styles.reportsContainer}>
      <Text style={styles.comingSoon}>Reports & Analytics</Text>
      <Text style={styles.comingSoonDesc}>Detailed project reports coming soon</Text>
    </View>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'timeline': return renderTimelineView();
      case 'gantt': return renderGanttView();
      case 'calendar': return renderCalendarView();
      case 'team': return renderTeamView();
      case 'reports': return renderReportsView();
      default: return renderTimelineView();
    }
  };

  return (
    <SafeAreaView style={styles.container}>
      <StatusBar barStyle="light-content" backgroundColor="#3B82F6" />
      
      {/* Header with Company Logo */}
      <LinearGradient colors={['#3B82F6', '#1D4ED8']} style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <MaterialCommunityIcons name="arrow-left" size={24} color="#FFFFFF" />
        </TouchableOpacity>
        
        <View style={styles.headerCenter}>
          {companyLogo ? (
            <Image source={{ uri: companyLogo || undefined }} style={styles.headerLogo} />
          ) : (
            <MaterialCommunityIcons name="office-building" size={24} color="#FFFFFF" />
          )}
          <Text style={styles.headerTitle}>Project Schedule</Text>
        </View>
        
        <TouchableOpacity style={styles.menuButton}>
          <MaterialCommunityIcons name="dots-vertical" size={24} color="#FFFFFF" />
        </TouchableOpacity>
      </LinearGradient>

      {/* Project Overview */}
      <View style={styles.projectOverview}>
        <View style={styles.overviewItem}>
          <MaterialCommunityIcons name="calendar-range" size={20} color="#3B82F6" />
          <Text style={styles.overviewLabel}>Duration</Text>
          <Text style={styles.overviewValue}>
            {formatDate(projectData.startDate)} - {formatDate(projectData.endDate)}
          </Text>
        </View>
        <View style={styles.overviewItem}>
          <MaterialCommunityIcons name="progress-check" size={20} color="#10B981" />
          <Text style={styles.overviewLabel}>Progress</Text>
          <Text style={styles.overviewValue}>{projectData.progress}%</Text>
        </View>
        <View style={styles.overviewItem}>
          <MaterialCommunityIcons name="account-group" size={20} color="#8B5CF6" />
          <Text style={styles.overviewLabel}>Team</Text>
          <Text style={styles.overviewValue}>{projectData.team.length} members</Text>
        </View>
      </View>

      {/* Tab Navigation */}
      <View style={styles.tabContainer}>
        <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.tabScrollContainer}>
          {tabs.map((tab) => (
            <TouchableOpacity
              key={tab.id}
              style={[styles.tab, activeTab === tab.id && styles.activeTab]}
              onPress={() => setActiveTab(tab.id)}
            >
              <MaterialCommunityIcons 
                name={tab.icon} 
                size={20} 
                color={activeTab === tab.id ? '#3B82F6' : '#6B7280'} 
              />
              <Text style={[styles.tabText, activeTab === tab.id && styles.activeTabText]}>
                {tab.name}
              </Text>
            </TouchableOpacity>
          ))}
        </ScrollView>
      </View>

      {/* Tab Content */}
      <Animated.View 
        style={[
          styles.content,
          {
            opacity: fadeAnim,
            transform: [{ translateY: slideAnim }]
          }
        ]}
      >
        <ScrollView showsVerticalScrollIndicator={false}>
          {renderTabContent()}
        </ScrollView>
      </Animated.View>

      {/* Add Task Modal */}
      <Modal
        visible={showTaskModal}
        animationType="slide"
        presentationStyle="pageSheet"
      >
        <SafeAreaView style={styles.modalContainer}>
          <View style={styles.modalHeader}>
            <TouchableOpacity onPress={() => setShowTaskModal(false)}>
              <Text style={styles.modalCancel}>Cancel</Text>
            </TouchableOpacity>
            <Text style={styles.modalTitle}>Add New Task</Text>
            <TouchableOpacity onPress={addNewTask}>
              <Text style={styles.modalSave}>Save</Text>
            </TouchableOpacity>
          </View>
          
          <ScrollView style={styles.modalContent}>
            <View style={styles.inputGroup}>
              <Text style={styles.inputLabel}>Task Title</Text>
              <TextInput
                style={styles.textInput}
                value={newTask.title}
                onChangeText={(text) => setNewTask({...newTask, title: text})}
                placeholder="Enter task title"
              />
            </View>
            
            <View style={styles.inputGroup}>
              <Text style={styles.inputLabel}>Description</Text>
              <TextInput
                style={[styles.textInput, styles.textArea]}
                value={newTask.description}
                onChangeText={(text) => setNewTask({...newTask, description: text})}
                placeholder="Enter task description"
                multiline
                numberOfLines={3}
              />
            </View>
            
            <View style={styles.inputRow}>
              <View style={styles.inputHalf}>
                <Text style={styles.inputLabel}>Assignee</Text>
                <TextInput
                  style={styles.textInput}
                  value={newTask.assignee}
                  onChangeText={(text) => setNewTask({...newTask, assignee: text})}
                  placeholder="Team member"
                />
              </View>
              
              <View style={styles.inputHalf}>
                <Text style={styles.inputLabel}>Priority</Text>
                <View style={styles.prioritySelector}>
                  {['Low', 'Medium', 'High'].map((priority) => (
                    <TouchableOpacity
                      key={priority}
                      style={[
                        styles.priorityOption,
                        newTask.priority === priority && styles.selectedPriority
                      ]}
                      onPress={() => setNewTask({...newTask, priority})}
                    >
                      <Text style={[
                        styles.priorityOptionText,
                        newTask.priority === priority && styles.selectedPriorityText
                      ]}>
                        {priority}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </View>
              </View>
            </View>
            
            <View style={styles.inputRow}>
              <View style={styles.inputHalf}>
                <Text style={styles.inputLabel}>Start Date</Text>
                <TextInput
                  style={styles.textInput}
                  value={newTask.startDate}
                  onChangeText={(text) => setNewTask({...newTask, startDate: text})}
                  placeholder="YYYY-MM-DD"
                />
              </View>
              
              <View style={styles.inputHalf}>
                <Text style={styles.inputLabel}>End Date</Text>
                <TextInput
                  style={styles.textInput}
                  value={newTask.endDate}
                  onChangeText={(text) => setNewTask({...newTask, endDate: text})}
                  placeholder="YYYY-MM-DD"
                />
              </View>
            </View>
            
            <View style={styles.inputGroup}>
              <Text style={styles.inputLabel}>Estimated Hours</Text>
              <TextInput
                style={styles.textInput}
                value={newTask.estimatedHours}
                onChangeText={(text) => setNewTask({...newTask, estimatedHours: text})}
                placeholder="Enter estimated hours"
                keyboardType="numeric"
              />
            </View>
          </ScrollView>
        </SafeAreaView>
      </Modal>

      {/* Add Milestone Modal */}
      <Modal
        visible={showMilestoneModal}
        animationType="slide"
        presentationStyle="pageSheet"
      >
        <SafeAreaView style={styles.modalContainer}>
          <View style={styles.modalHeader}>
            <TouchableOpacity onPress={() => setShowMilestoneModal(false)}>
              <Text style={styles.modalCancel}>Cancel</Text>
            </TouchableOpacity>
            <Text style={styles.modalTitle}>Add Milestone</Text>
            <TouchableOpacity onPress={addNewMilestone}>
              <Text style={styles.modalSave}>Save</Text>
            </TouchableOpacity>
          </View>
          
          <ScrollView style={styles.modalContent}>
            <View style={styles.inputGroup}>
              <Text style={styles.inputLabel}>Milestone Title</Text>
              <TextInput
                style={styles.textInput}
                value={newMilestone.title}
                onChangeText={(text) => setNewMilestone({...newMilestone, title: text})}
                placeholder="Enter milestone title"
              />
            </View>
            
            <View style={styles.inputGroup}>
              <Text style={styles.inputLabel}>Description</Text>
              <TextInput
                style={[styles.textInput, styles.textArea]}
                value={newMilestone.description}
                onChangeText={(text) => setNewMilestone({...newMilestone, description: text})}
                placeholder="Enter milestone description"
                multiline
                numberOfLines={3}
              />
            </View>
            
            <View style={styles.inputGroup}>
              <Text style={styles.inputLabel}>Target Date</Text>
              <TextInput
                style={styles.textInput}
                value={newMilestone.date}
                onChangeText={(text) => setNewMilestone({...newMilestone, date: text})}
                placeholder="YYYY-MM-DD"
              />
            </View>
          </ScrollView>
        </SafeAreaView>
      </Modal>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F9FAFB',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 16,
    paddingTop: 20,
  },
  backButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  headerCenter: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    marginLeft: 16,
  },
  headerLogo: {
    width: 30,
    height: 30,
    borderRadius: 15,
    marginRight: 8,
    resizeMode: 'cover',
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#FFFFFF',
  },
  menuButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  projectOverview: {
    flexDirection: 'row',
    backgroundColor: '#FFFFFF',
    paddingVertical: 16,
    paddingHorizontal: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
  },
  overviewItem: {
    flex: 1,
    alignItems: 'center',
  },
  overviewLabel: {
    fontSize: 12,
    color: '#6B7280',
    marginTop: 4,
  },
  overviewValue: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1F2937',
    marginTop: 2,
    textAlign: 'center',
  },
  tabContainer: {
    backgroundColor: '#FFFFFF',
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
  },
  tabScrollContainer: {
    paddingHorizontal: 20,
  },
  tab: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 16,
    marginRight: 8,
    borderRadius: 8,
  },
  activeTab: {
    backgroundColor: '#EFF6FF',
  },
  tabText: {
    fontSize: 14,
    color: '#6B7280',
    marginLeft: 6,
  },
  activeTabText: {
    color: '#3B82F6',
    fontWeight: '600',
  },
  content: {
    flex: 1,
  },
  timelineContainer: {
    padding: 20,
  },
  healthCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 20,
    marginBottom: 24,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  healthTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#1F2937',
    marginBottom: 16,
  },
  healthMetrics: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  healthMetric: {
    alignItems: 'center',
    flex: 1,
  },
  healthLabel: {
    fontSize: 12,
    color: '#6B7280',
    marginTop: 4,
  },
  healthValue: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#1F2937',
    marginTop: 2,
  },
  section: {
    marginBottom: 32,
  },
  sectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#1F2937',
  },
  addButton: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: '#EFF6FF',
    justifyContent: 'center',
    alignItems: 'center',
  },
  milestonesContainer: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  milestoneItem: {
    flexDirection: 'row',
    marginBottom: 20,
  },
  milestoneIndicator: {
    alignItems: 'center',
    marginRight: 16,
  },
  milestoneDot: {
    width: 12,
    height: 12,
    borderRadius: 6,
  },
  milestoneLine: {
    width: 2,
    height: 40,
    backgroundColor: '#E5E7EB',
    marginTop: 8,
  },
  milestoneContent: {
    flex: 1,
  },
  milestoneHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 4,
  },
  milestoneTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1F2937',
  },
  milestoneDate: {
    fontSize: 12,
    color: '#6B7280',
  },
  milestoneDescription: {
    fontSize: 14,
    color: '#6B7280',
    marginBottom: 8,
    lineHeight: 20,
  },
  milestoneStatus: {
    alignSelf: 'flex-start',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
  },
  milestoneStatusText: {
    fontSize: 10,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  taskCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  taskHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 8,
  },
  taskInfo: {
    flex: 1,
    marginRight: 12,
  },
  taskTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1F2937',
    marginBottom: 4,
  },
  taskAssignee: {
    fontSize: 12,
    color: '#6B7280',
  },
  taskMeta: {
    alignItems: 'flex-end',
    gap: 8,
  },
  priorityBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
  },
  priorityText: {
    fontSize: 10,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  statusBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
  },
  statusText: {
    fontSize: 10,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  taskDescription: {
    fontSize: 14,
    color: '#6B7280',
    marginBottom: 12,
    lineHeight: 20,
  },
  taskProgress: {
    marginBottom: 12,
  },
  progressInfo: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  progressLabel: {
    fontSize: 12,
    fontWeight: '600',
    color: '#1F2937',
  },
  progressDates: {
    fontSize: 12,
    color: '#6B7280',
  },
  progressBar: {
    height: 6,
    backgroundColor: '#E5E7EB',
    borderRadius: 3,
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    borderRadius: 3,
  },
  taskFooter: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  taskHours: {
    fontSize: 12,
    color: '#6B7280',
  },
  ganttContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 40,
  },
  calendarContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 40,
  },
  teamContainer: {
    padding: 20,
  },
  teamMemberCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
    flexDirection: 'row',
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  memberInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  memberAvatar: {
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: '#3B82F6',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  memberInitials: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#FFFFFF',
  },
  memberDetails: {
    flex: 1,
  },
  memberName: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1F2937',
  },
  memberRole: {
    fontSize: 14,
    color: '#6B7280',
    marginTop: 2,
  },
  workloadInfo: {
    alignItems: 'flex-end',
    minWidth: 80,
  },
  workloadLabel: {
    fontSize: 12,
    color: '#6B7280',
    marginBottom: 4,
  },
  workloadBar: {
    width: 60,
    height: 6,
    backgroundColor: '#E5E7EB',
    borderRadius: 3,
    overflow: 'hidden',
    marginBottom: 4,
  },
  workloadFill: {
    height: '100%',
    borderRadius: 3,
  },
  workloadValue: {
    fontSize: 12,
    fontWeight: '600',
    color: '#1F2937',
  },
  reportsContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 40,
  },
  comingSoon: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#1F2937',
    marginBottom: 8,
  },
  comingSoonDesc: {
    fontSize: 16,
    color: '#6B7280',
    textAlign: 'center',
  },
  modalContainer: {
    flex: 1,
    backgroundColor: '#FFFFFF',
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
  },
  modalCancel: {
    fontSize: 16,
    color: '#6B7280',
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#1F2937',
  },
  modalSave: {
    fontSize: 16,
    color: '#3B82F6',
    fontWeight: '600',
  },
  modalContent: {
    flex: 1,
    padding: 20,
  },
  inputGroup: {
    marginBottom: 20,
  },
  inputLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1F2937',
    marginBottom: 8,
  },
  textInput: {
    borderWidth: 1,
    borderColor: '#D1D5DB',
    borderRadius: 8,
    padding: 12,
    fontSize: 16,
    color: '#1F2937',
  },
  textArea: {
    height: 80,
    textAlignVertical: 'top',
  },
  inputRow: {
    flexDirection: 'row',
    gap: 12,
    marginBottom: 20,
  },
  inputHalf: {
    flex: 1,
  },
  prioritySelector: {
    flexDirection: 'row',
    gap: 8,
  },
  priorityOption: {
    flex: 1,
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#D1D5DB',
    alignItems: 'center',
  },
  selectedPriority: {
    backgroundColor: '#3B82F6',
    borderColor: '#3B82F6',
  },
  priorityOptionText: {
    fontSize: 14,
    color: '#6B7280',
  },
  selectedPriorityText: {
    color: '#FFFFFF',
    fontWeight: '600',
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
    backgroundColor: '#F9FAFB',
  },
  errorTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#1F2937',
    marginTop: 16,
    marginBottom: 8,
  },
  errorMessage: {
    fontSize: 16,
    color: '#6B7280',
    textAlign: 'center',
    marginBottom: 24,
    lineHeight: 24,
  },
  errorButton: {
    backgroundColor: '#3B82F6',
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 8,
  },
  errorButtonText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '600',
  },
});